import { Checkbox, FormControlLabel } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { LatLngBoundsExpression, LatLngExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LayersControl, MapContainer, TileLayer, useMap } from "react-leaflet";
import { useLocation } from "react-router-dom";
import { DeviceWithState } from "../Device";
import { isDeviceStateRecent } from "../Helper";
import { DeviceMarker } from "../components/DeviceMarker";
import InfoIcon from "../components/InfoIcon";

interface MapPageProps {
  devicesWithState: DeviceWithState[]
}

const MapPage = (props: MapPageProps): JSX.Element => {
  const { devicesWithState } = props;
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const devicesNotIgnored = devicesWithState.filter(d => !d.deviceDescriptor.isIgnored);

  const deviceWithStateFromUrl = useMemo(() => {
    const deviceIdStringFromUrl = new URLSearchParams(location.search).get('deviceId');
    if (deviceIdStringFromUrl === null) {
      return undefined;
    }
    return devicesNotIgnored.find(d => d.deviceDescriptor.deviceId === Number(deviceIdStringFromUrl));
  }, [location.search, devicesNotIgnored]);

  //we recalculate the fit bounds if
  // - on page load, provided that there is no device with state in the URL
  // - or when the checkbox is toggled to show/hide old devices
  //this prevents the fitBound method from being called unnecessarily every time the map is rendered (every 5s currently)
  const [recalculateFitBounds, setRecalculateFitBounds] = useState(deviceWithStateFromUrl === undefined);

  useEffect(() => {
    if (recalculateFitBounds) {
      setRecalculateFitBounds(false); // Reset the flag after the render with fitBounds immediately
    }
  }, [recalculateFitBounds]);

  const recentDevices = devicesNotIgnored.filter(d => isDeviceStateRecent(d.deviceState));
  const [showOldDevice, setShowOldDevice] = useState(
    (deviceWithStateFromUrl !== undefined && !recentDevices.find(d => d.deviceDescriptor.deviceId === deviceWithStateFromUrl?.deviceDescriptor.deviceId)) // If the device from the URL is not recent, we show all devices by default
    || recentDevices.length === 0); // If there are no recent devices, we show all devices by default

  // Devices to show on the map based on the checkbox state and their coordinates availability
  const devicesToShow = useMemo(() => {
    return (showOldDevice ? devicesNotIgnored : recentDevices).filter(device =>
      device.deviceState.latitude !== undefined &&
      device.deviceState.longitude !== undefined &&
      !isNaN(device.deviceState.latitude) &&
      !isNaN(device.deviceState.longitude)
    );
  }, [devicesNotIgnored, recentDevices, showOldDevice]);

  // This is only relevant if devicesToShow is empty or there is a device with state in the URL
  const zoom = useMemo(() => {
    const MAX_ZOOM_LEVEL = 18; // Max zoom level of Leaflet. The higher the number, the more we are zoomed in.

    if (deviceWithStateFromUrl !== undefined) {
      return MAX_ZOOM_LEVEL; // If a device is specified in the URL, we zoom in on it
    }
    // Default zoom level in case there is no device to show. Europe if EN size of Poland, Germany and Hungary otherwise
    switch (i18n.language) {
      case 'hu':
        return 8;
      case 'de':
        return 6;
      case 'pl':
        return 7;
      default: return 4; // Default zoom level for English
    }
  }, [deviceWithStateFromUrl, i18n.language]);

  // This is only relevant if devicesToShow is empty or there is a device with state in the URL
  const center: LatLngExpression = useMemo(() => {
    //If a device is specified in the URL with valid coordinates, center the map on it
    if (deviceWithStateFromUrl !== undefined &&
      deviceWithStateFromUrl.deviceState.latitude !== undefined &&
      deviceWithStateFromUrl.deviceState.longitude !== undefined &&
      !isNaN(deviceWithStateFromUrl.deviceState.latitude) &&
      !isNaN(deviceWithStateFromUrl.deviceState.longitude)) {
      return [deviceWithStateFromUrl.deviceState.latitude, deviceWithStateFromUrl.deviceState.longitude];
    }

    // Default center based on locale
    switch (i18n.language) {
      case 'hu':
        return [46.850384, 19.083496];
      case 'de':
        return [51.1657, 10.4515];
      case 'pl':
        return [52.2297, 21.0122];
      default: return [53.5775, 23.1061]; // Default center for English, roughly in the middle of Europe
    }
  }, [deviceWithStateFromUrl, i18n.language])

  //This is a react function component that allows us to fit the map bounds to the coordinates of the devices shown
  //using the fitBounds method of the Leaflet map.
  const FitBounds = ({ coords }: { coords: number[][] }) => {
    const map = useMap();//this can only be used in a component that is a child of MapContainer

    if (coords.length > 0) {
      const bounds: LatLngBoundsExpression = [
        [Math.min(...coords.map(([lat, lng]) => lat)), Math.min(...coords.map(([lat, lng]) => lng))],
        [Math.max(...coords.map(([lat, lng]) => lat)), Math.max(...coords.map(([lat, lng]) => lng))]
      ];
      map.fitBounds(bounds);
    }

    return null;
  }

  const markersToShow = useMemo(() =>
    devicesToShow.map((deviceWithState, index) => (<DeviceMarker key={index} deviceWithState={deviceWithState} />))
    , [devicesToShow]);

  return <div style={{ height: "80vh" }}>
    <Grid container alignItems="center">
      <Grid>
        <FormControlLabel
          control={<Checkbox
            checked={!showOldDevice}
            color="primary"
            onChange={() => {
              setShowOldDevice(v => !v);
              setRecalculateFitBounds(true); // Recalculate fit bounds when toggling the checkbox
            }}
          />}
          label={t('map.only_show_recent_text')} />
      </Grid>
      <Grid>
        <InfoIcon infoLabel="map.only_show_recent_tooltip" />
      </Grid>
    </Grid>
    <MapContainer
      center={center}
      zoom={zoom}
      style={{ height: "100%" }}>
      <LayersControl position="topright">
        <LayersControl.BaseLayer checked name="Satellite">
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Street">
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
        </LayersControl.BaseLayer>
      </LayersControl>
      {/*devicesToShow all have their coordinates defined */}
      {recalculateFitBounds && <FitBounds coords={devicesToShow.map(device => [device.deviceState.latitude!, device.deviceState.longitude!])} />}
      {markersToShow}
    </MapContainer>
  </div>
}

export default MapPage;
